import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';

/** Matches the dwell time every other saved-report toast in the app uses, so feedback on this surface
 * reads the same as saving a Standard report, and the save and download toasts do not linger for
 * different lengths of time. */
const TOAST_DURATION_MS = 5000;

/** Off-screen but still rendered, so a screen reader reaches the text while nothing is shown on
 * screen. `display: none` and `visibility: hidden` would take it out of the accessibility tree too, and
 * so would `width: 0`, which is why this clips a 1px box instead of collapsing one. */
const ScreenReaderOnly = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	border: 0;
	overflow: hidden;
	clip: rect(0 0 0 0);
	clip-path: inset(50%);
	white-space: nowrap;
`;

export interface ReportExport {
	/** True while an export is in flight. Both bars close their Download control off while set. */
	exporting: boolean;
	/** Visible Download label, which becomes the busy wording while an export runs. */
	label: string;
	/** Starts an export and raises a warning toast if it fails. Never throws. */
	start: () => void;
	/**
	 * Live region announcing the busy state. Render it next to the Download control.
	 *
	 * A full-dataset export is slow enough to need feedback, and on screen it gets it: the label changes
	 * and the control closes off. Neither is announced - a screen reader is not told that a control it
	 * is not focused on has changed - so the press would appear to do nothing at all until the file
	 * arrives. The failure path already announces itself, because the toaster is a polite live region.
	 */
	statusRegion: JSX.Element;
}

/**
 * The Download control's behaviour, shared by both top bars so the two cannot drift apart.
 *
 * The bars render Download differently on purpose - edit mode uses a field-style button with an inline
 * chevron, view mode a split button with a caret section - but what pressing it does, how the busy
 * state reads, and how a failure is reported are the same in both, and were duplicated line for line.
 */
export function useReportExport(): ReportExport {
	const {formatMessage} = useIntl();
	const {exportCsv, exporting} = useReportBuilder();

	// A successful export announces itself - the browser hands the user a file - so only a failure is
	// worth a toast. `exportCsv()` resolves an outcome rather than rejecting, so there is nothing to catch.
	//
	// 'too_large' gets its own wording because the generic copy tells the user to try again, and the
	// backend refuses an over-ceiling export deterministically: the same report will be refused every
	// time, so "try again" would send them round a loop. Narrowing the report is the only way through.
	const start = (): void => {
		void exportCsv().then(outcome => {
			if (outcome === 'exported') {
				return;
			}
			createToast({
				duration: TOAST_DURATION_MS,
				warning: true,
				message: formatMessage({
					id: outcome === 'too_large' ? 'ai_report_builder.export_too_large' : 'ai_report_builder.export_failed',
				}),
			});
		});
	};

	// Resolved to a string rather than left as a <FormattedMessage>: it is also the control's accessible
	// name, and the visible label and that name must stay the same words.
	const label = exporting
		? formatMessage({id: 'ai_report_builder.downloading'})
		: formatMessage({id: 'ai_report_builder.download'});

	const statusRegion = (
		<ScreenReaderOnly role={'status'} aria-live={'polite'} data-cy={'ai-report-export-status'}>
			{exporting ? formatMessage({id: 'ai_report_builder.downloading'}) : ''}
		</ScreenReaderOnly>
	);

	return {exporting, label, start, statusRegion};
}
